/**
 * Dashboard — the "Terminal of Nodes" main screen.
 *
 * Layered Liquid Glass cards over the cosmic background mesh. Stagger
 * entry animation runs once on mount via Motion's `motion.div` with
 * a parent variants set so child cards rise in sequence.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'motion/react';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  GlassSurface,
  GlassPill,
  LiquidNumber,
  GlassButton,
  ClickableCard,
  InfoTooltip,
} from '@/components/glass';
import { NodeOrbit, type BurstFire } from '@/components/nodes/NodeOrbit';
import { LiveFeedTicker } from '@/components/nodes/LiveFeedTicker';
import { TerminalLog } from '@/components/nodes/TerminalLog';
import { LiveStatsStrip } from '@/components/nodes/LiveStatsStrip';
import { BusinessActivity } from '@/components/nodes/BusinessActivity';
import { NodeInfoPopup } from '@/components/nodes/NodeInfoPopup';
import { ClaimModal } from '@/components/nodes/ClaimModal';
import { ClaimStatusPill } from '@/components/nodes/ClaimStatusPill';
import { PcAutoInfoModal } from '@/components/nodes/PcAutoInfoModal';
import { ConnectionStatusProvider } from '@/contexts/ConnectionStatusContext';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useDashboardUser } from '@/hooks/useDashboardUser';
import { useNarrowScreen } from '@/hooks/useNarrowScreen';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardFire, useNetworkEvent } from '@/hooks/useDashboardFire';
import { BUSINESSES } from '@/data/businesses';
import type { DashboardUserData } from '@/hooks/useDashboardUser';

// Пустой дашборд: показываем нули пока грузится с сервера. Никаких моков.
const EMPTY_DASHBOARD: DashboardUserData = {
  balance: '0',
  todayReward: 0,
  todayChangePct: 0,
  month30Reward: 0,
  totalLps: 0,
  contractName: null,
  contractMonthly: null,
  contractProgress: 0,
  activeContractsCount: 0,
  slotsSummary: [],
  refIncome: 0,
  refLinesOpen: 0,
  pendingProfit: 0,
  manualPendingProfit: 0,
  pcContracts: [],
  cycleAccumulated: 0,
  cycleSecondsLeft: 0,
  claimReady: false,
  plasmaCoreActive: false,
  plasmaCoreUntil: null,
  pcAvgChargePct: null,
  pcContractsCount: 0,
  manualContractsCount: 0,
  pcNextClaimMs: null,
  pcLifetimeEarned: 0,
  businesses: [],
  feed: [],
};

// Сводка по слотам: каждый тип на своей строке, остаток (+N ещё)
// прижат к правому краю последней видимой строки. Клик — /slots.
const SLOTS_VISIBLE = 2;
type SlotSummaryEntry = { name: string; count: number; totalInvested: number };

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      delayChildren: 0.05,
      staggerChildren: 0.08,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.32, 0.72, 0, 1] },
  },
};

export function Dashboard() {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const navigate = useNavigate();
  const rawServerFire = useDashboardFire();
  // Сетевая активность платформы (реальные клеймы юзеров ≥ $0.50)
  const networkEvent = useNetworkEvent();

  // Стейты модалок поднимаем выше useDashboardUser — чтобы передать
  // `paused` и приостановить 30-сек polling пока модалка открыта.
  // Иначе apiData refetch меняет ссылки на pcContracts/etc → motion
  // перестраивается и модалка визуально «моргает» на iOS WebView.
  const [claimOpen, setClaimOpen] = useState(false);
  const [pcInfoOpen, setPcInfoOpen] = useState(false);
  const anyModalOpen = claimOpen || pcInfoOpen;

  // Реальные данные с бэкенда. Пока грузится — рендерим safe-zero.
  // Никаких моков: показываем 0, а не выдуманные значения.
  const [apiData, refetchDashboard] = useDashboardUser({
    withRefetch: true,
    paused: anyModalOpen,
  });
  const { refresh: refreshAuth, applyBalanceDelta } = useAuth();
  const data = apiData ?? EMPTY_DASHBOARD;

  // ──────────────────────────────────────────────────────────────────
  // Live earning state — updated by NodeOrbit's onFire callback at
  // STAGE 3 of the cycle (after the node fire and core pulse). At
  // that exact moment we:
  //   1) tick liveToday up by `amount`
  //   2) push the event into the LiveFeedTicker
  //   3) flash the balance number (CSS pulse via ref + class swap)
  //   4) fire a soft haptic so the user *feels* the income land
  //
  // Stage 3 is the only place state updates happen — stages 1 & 2
  // are pure CSS animations that never re-render React.
  // ──────────────────────────────────────────────────────────────────
  // ── Pending profit + live drip ────────────────────────────────
  // pendingProfit = реальные данные из API (обновляется каждые 30с)
  // liveDrip = локальная прибавка от fire-анимаций между API-полингами
  // liveToday = pendingProfit + liveDrip (отображается в счётчике)
  const [pendingProfit, setPendingProfit] = useState(0);
  const [liveDrip, setLiveDrip] = useState(0);
  const [cycleSecondsLeft, setCycleSecondsLeft] = useState(data.cycleSecondsLeft ?? 0);
  // Локальный override чтобы после ручного claim счётчик мгновенно
  // показал 0 — не ждать 30с до следующего poll'а. Сбрасывается в
  // null когда новые apiData прилетают (они уже с обнулённым
  // cycleAccumulated, т.к. бэк сдвинул last_global_claim_at).
  const [cycleAccumOverride, setCycleAccumOverride] = useState<number | null>(null);

  // ── Entry burst ────────────────────────────────────────────────
  // При первом получении данных с API: создаём burst из pendingProfit.
  // Счётчик стартует с 0, burst-fires "наполняют" его через liveDrip.
  // После burst: pendingProfit устанавливается из API, liveDrip = 0.
  const [burstFires, setBurstFires] = useState<BurstFire[] | null>(null);
  const burstDoneRef = useRef(false);
  const firstApiRef = useRef(false);

  // Суточный доход — считаем при обновлении API
  const dailyIncomeRef = useRef(0);

  // Гейт «лоадер закрылся». Burst-анимация и ноды начинаются ТОЛЬКО
  // после того как loading-screen ушёл — иначе эффекты играют под ним
  // и юзер их не видит. Флаг живёт в sessionStorage + listener.
  const [loaderDone, setLoaderDone] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('luna_loader_done') === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (loaderDone) return;
    const handler = () => setLoaderDone(true);
    window.addEventListener('luna:loader-done', handler);
    return () => window.removeEventListener('luna:loader-done', handler);
  }, [loaderDone]);

  useEffect(() => {
    if (!apiData) return;
    // Не трогаем burst-fires пока лоадер не закроется — иначе вся
    // entry-burst анимация играет под чёрным оверлеем.
    if (!loaderDone) return;

    setCycleSecondsLeft(apiData.cycleSecondsLeft);

    // Экстраполируем суточный доход
    if (apiData.activeContractsCount > 0) {
      const elapsed = 86400 - (apiData.cycleSecondsLeft ?? 86400);
      if (elapsed > 60) {
        dailyIncomeRef.current = apiData.pendingProfit * (86400 / elapsed);
      }
    } else {
      dailyIncomeRef.current = 0;
    }

    // ПЕРВЫЙ ответ API: создаём entry burst.
    // НЕ ставим pendingProfit — burst fires заполнят liveDrip от 0 до profit.
    // Следующий API-poll (через 30с) сам выровняет: pendingProfit = API, liveDrip = 0.
    // Это исключает двойной счёт (0.19 liveDrip + 0.19 pendingProfit = 0.38 — БАГ).
    if (!firstApiRef.current) {
      firstApiRef.current = true;
      const profit = apiData.pendingProfit;

      if (profit > 0.001) {
        // Берём бизнесы из API — каждый имеет реальную долю profit.
        // Выбираем 3-6 бизнесов с наибольшим reward для burst fires.
        // Что вылетает из ноды = то что на шкале бизнеса. Всё совпадает.
        const apiBiz = apiData.businesses as Array<{
          short: string; reward: number; share: number;
        }>;
        const withReward = apiBiz
          .map((b, i) => ({ ...b, nodeIdx: i }))
          .filter((b) => b.reward > 0.0001)
          .sort((a, b) => b.reward - a.reward);

        const numFires = Math.min(
          withReward.length,
          Math.max(3, Math.min(6, 3 + Math.floor(Math.random() * 4))),
        );
        const selected = withReward.slice(0, numFires);

        // Остаток от невыбранных бизнесов добавим к последнему fire
        const selectedTotal = selected.reduce((s, b) => s + b.reward, 0);
        const remainder = profit - selectedTotal;

        const fires: BurstFire[] = selected.map((b, i) => ({
          nodeIdx: b.nodeIdx,
          amount: i === selected.length - 1
            ? b.reward + Math.max(0, remainder)
            : b.reward,
        }));

        setBurstFires(fires);
        // pendingProfit остаётся 0. liveDrip вырастет через onFire до ~profit.
        // Когда API обновится через 30с, произойдёт: pendingProfit=API, liveDrip=0.
        // Счётчик: ~profit → profit (без скачка).
      } else {
        // Нет accumulated profit — просто ставим значения
        burstDoneRef.current = true;
        setPendingProfit(apiData.pendingProfit);
      }
    } else {
      // Последующие API-обновления (каждые 30с)
      setPendingProfit(apiData.pendingProfit);
      setLiveDrip(0);
      // Override больше не нужен — бэк вернул свежее cycleAccumulated
      // (после ручного claim бэк сдвинул last_global_claim_at, SUM=0).
      setCycleAccumOverride(null);
      burstDoneRef.current = true;
    }
  }, [apiData, loaderDone]);

  // Счётчик «Генерация прибыли» = pending (текущий цикл) + liveDrip
  // (локальная подкапка между polls) + cycleAccumulated (сумма уже
  // выплаченных PC-авто-клеймов с момента последнего ручного клейма).
  //
  // cycleAccumulated делает счётчик монотонным: когда PC-слот авто-
  // клеймится, его pending обнуляется на бэке, но cycleAccumulated
  // вырастает на ту же сумму — сумма в UI не дёргается вниз.
  // Сбрасывается только при ручном claim (см. handleClaim).
  // cycleAccumulated раньше складывалось в счётчик чтобы он «не откатывался»
  // вниз при PC автоклейме. Убрано по запросу админа (2026-04-24): после
  // автосбора деньги УЖЕ на балансе — они не должны висеть в «Генерация
  // прибыли» как незабранные. Счётчик теперь = то что реально накоплено
  // в данный момент (manual pending + PC pending следующего цикла).
  void cycleAccumOverride;
  const liveToday = Math.round(
    (pendingProfit + liveDrip) * 1000,
  ) / 1000;

  // currentEvent больше не нужен — LiveFeedTicker слушает networkEvent
  // напрямую через SSE. NodeOrbit fire-колбэк просто кормит счётчик.
  const balanceRef = useRef<HTMLDivElement>(null);

  // ── Tap interactions ─────────────────────────────────────────
  const [infoIdx, setInfoIdx] = useState<number | null>(null);
  // claimOpen/pcInfoOpen объявлены выше (нужны для useDashboardUser pause)
  const isNarrow = useNarrowScreen(400);

  const hasContracts = (apiData?.activeContractsCount ?? 0) > 0;
  const claimReady = cycleSecondsLeft === 0 && (apiData?.claimReady ?? false);
  const plasmaCoreActive = apiData?.plasmaCoreActive ?? false;
  // PC charge % — сколько осталось от 7-дневного цикла. Когда 0 → нужна перезарядка.
  const plasmaCoreCharge = (() => {
    const until = apiData?.plasmaCoreUntil;
    if (!until || !plasmaCoreActive) return 0;
    const msLeft = until - Date.now();
    if (msLeft <= 0) return 0;
    const cycleMs = 7 * 24 * 60 * 60 * 1000;
    return Math.max(0, Math.min(100, Math.round((msLeft / cycleMs) * 100)));
  })();

  // Cycle countdown — runs continuously, not only when modal is open.
  // Drives the ClaimStatusPill so the user always sees the live timer.
  useEffect(() => {
    const id = window.setInterval(() => {
      setCycleSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const handleTapNode = useCallback((idx: number) => {
    setInfoIdx(idx);
    haptic.light();
  }, [haptic]);

  const handleTapCenter = useCallback(() => {
    // Не открываем Claim если нет контрактов
    if ((apiData?.activeContractsCount ?? 0) === 0) {
      haptic.light();
      return;
    }
    setClaimOpen(true);
    haptic.medium();
  }, [haptic, apiData]);

  // Called by ClaimModal AFTER its 3-second success animation finishes
  // Деньги "перелетают" из счётчика → в баланс. Рефрешим /auth/me
  // (usdtBalance в хедере) и /me/dashboard (баланс в дашборде).
  const handleClaim = useCallback((credited: number) => {
    // Сбрасываем счётчик pending + drip + накопленные PC-выплаты.
    // cycleAccumOverride держит 0 до следующего poll'а с бэка
    // (там last_global_claim_at уже обновился → cycleAccumulated=0).
    setPendingProfit(0);
    setLiveDrip(0);
    setCycleAccumOverride(0);
    // Новый цикл 24ч
    setCycleSecondsLeft(86400);
    setClaimOpen(false);
    // Flash the dashboard balance line one more time
    const el = balanceRef.current;
    if (el) {
      el.classList.remove('balance-tick');
      void el.offsetWidth;
      el.classList.add('balance-tick');
    }
    // Optimistic: прибавляем начисленное к локальному usdtBalance
    // чтобы хедер-баланс прыгнул мгновенно, не ждал refresh().
    // Следующий refreshAuth/refetchDashboard выровняет с бэком.
    if (credited > 0) {
      applyBalanceDelta(credited);
    }
    // Принудительный рефреш балансов чтобы UI подхватил клейм
    // без ожидания следующего polling-тика (30с).
    void refreshAuth();
    void refetchDashboard();
  }, [refreshAuth, refetchDashboard, applyBalanceDelta]);

  // Суточный доход = сумма(price_paid) * daily_rate (~0.004)
  // Steady fires каждые ~60с = 86400/60 = 1440 тиков/день
  // dripPerTick = dailyIncome / 1440
  // Нода и счётчик показывают ОДИНАКОВУЮ сумму (3 знака).
  // Минимум $0.001 — и на ноде, и в счётчике.
  const dripAmount = dailyIncomeRef.current > 0
    ? Math.max(dailyIncomeRef.current / 1440, 0.001)
    : 0;

  const handleNodeFire = useCallback(
    (amount: number, _idx: number) => {
      // amount = реальная сумма для счётчика (личный pendingProfit/dailyIncome)
      if (amount > 0) {
        setLiveDrip((prev) => prev + amount);
      }
      const el = balanceRef.current;
      if (el) {
        el.classList.remove('balance-tick');
        void el.offsetWidth;
        el.classList.add('balance-tick');
      }
      haptic.light();
    },
    [haptic],
  );

  return (
    <ConnectionStatusProvider>
    <motion.div
      className="space-y-5 max-w-md mx-auto pt-2"
      variants={containerVariants}
      initial="hidden"
      animate="show"
    >
      {/* ----- HERO: balance + node orbit ----- */}
      <motion.div variants={itemVariants}>
        <GlassSurface variant="strong" blur reactive className="p-6 relative">
          {/* Объяснение LPS / нод / бизнесов в верхнем правом углу.
              Сдвинут на -2px вверх и -5px вправо чтобы не наложился
              на пилюлю +0.15%. */}
          <InfoTooltip
            tone="plasma"
            className="!top-[-2px] !right-[3px]"
            ariaLabel={t('dashboard.lpsTooltip.ariaLabel')}
            title={t('dashboard.lpsTooltip.title')}
            body={
              <Trans
                i18nKey="dashboard.lpsTooltip.body"
                components={{ b: <strong />, br: <br /> }}
              />
            }
            bullets={[
              t('dashboard.lpsTooltip.bullet1'),
              t('dashboard.lpsTooltip.bullet2'),
              t('dashboard.lpsTooltip.bullet3'),
              t('dashboard.lpsTooltip.bullet4'),
            ]}
          />
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)]">
                  {t('dashboard.profitGeneration')}
                </p>
              </div>
              <div ref={balanceRef} className="inline-block">
                <LiquidNumber
                  value={liveToday}
                  decimals={2}
                  suffix={isNarrow ? '₮' : 'USD₮'}
                  className="text-[38px] max-[400px]:text-[30px] font-extrabold font-display text-[var(--text-primary)] tracking-tight"
                />
              </div>
            </div>
            {/* Накопленный % за сутки: плавно растёт от 0 в 00:00 МСК
                до дневной ставки в 23:59 МСК. Приходит с бэкенда как
                todayAccruedPct — зависит ТОЛЬКО от времени МСК-дня,
                не от моментов claim'а юзера. Формула:
                dailyRate * (прошедшее время МСК-дня / 24 часа). */}
            {(() => {
              const pct = data.todayAccruedPct ?? 0;
              return (
                <GlassPill
                  tone="positive"
                  dot
                  className="!mt-[40px] self-start"
                >
                  +{pct.toFixed(3)}%
                </GlassPill>
              );
            })()}
          </div>

          <NodeOrbit
            totalLps={data.totalLps}
            className="mt-4 mb-4"
            serverFire={hasContracts ? rawServerFire : null}
            dripAmount={hasContracts ? dripAmount : 0}
            burstFires={hasContracts ? burstFires : null}
            onFire={handleNodeFire}
            onTapNode={handleTapNode}
            onTapCenter={handleTapCenter}
          />

          {/* Hint если нет контрактов */}
          {!hasContracts && (
            <p className="text-[11px] text-[var(--text-muted)] text-center mt-1 mb-2">
              {t('dashboard.activateHint')}
            </p>
          )}

          {/* Claim state — gold pulsing pill when ready, countdown when waiting.
              Показывает ДВА таймера раздельно если у юзера есть и
              ручные, и PC-слоты (с подписями). */}
          <div className="mt-3">
            <ClaimStatusPill
              ready={claimReady}
              pendingProfit={data.manualPendingProfit}
              cycleSecondsLeft={cycleSecondsLeft}
              hasManualSlots={
                (data.activeContractsCount - data.pcContractsCount) > 0
              }
              pcNextClaimMs={data.pcNextClaimMs}
              pcContractsCount={data.pcContractsCount}
              onClick={() => {
                haptic.medium();
                setClaimOpen(true);
              }}
              onPcInfoClick={() => {
                haptic.light();
                setPcInfoOpen(true);
              }}
            />
          </div>

          {/* Live trading-floor ticker — скрыт если нет контрактов */}
          {hasContracts && (
            <div className="mt-3">
              <LiveFeedTicker
                networkEvent={networkEvent}
                onlineCount={data.onlineCount ?? 0}
              />
            </div>
          )}

          {/* Backend terminal — 4 rotating system messages */}
          <div className="mt-3 relative">
            <TerminalLog />
          </div>

          {/* Micro stats strip — TPS · LAT · UP */}
          <div className="mt-2 relative">
            <LiveStatsStrip />
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <SlotsSummaryCard
              summary={data.slotsSummary}
              onClick={() => navigate('/slots')}
            />
            <MiniStat
              label={t('dashboard.progress250')}
              value={data.activeContractsCount > 0 ? `${data.contractProgress}%` : '—'}
              accent
              progress={data.contractProgress}
              onClick={() => navigate('/slots')}
            />
          </div>
        </GlassSurface>
      </motion.div>

      {/* ----- REWARD SUMMARY ----- */}
      <motion.div className="grid grid-cols-2 gap-3" variants={itemVariants}>
        <ClickableCard
          onClick={() => navigate('/wallet')}
          className="p-4"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1.5">
            {t('dashboard.month30')}
          </p>
          <LiquidNumber
            value={data.month30Reward}
            decimals={2}
            suffix="USD₮"
            className="text-[20px] font-extrabold text-[var(--text-primary)]"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            {t('dashboard.perDay', { value: (data.month30Reward / 30).toFixed(2) })}
          </p>
        </ClickableCard>

        <ClickableCard
          onClick={() => navigate('/network')}
          className="p-4"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1.5">
            {t('dashboard.partners')}
          </p>
          <LiquidNumber
            value={data.refIncome}
            decimals={2}
            suffix="USD₮"
            className="text-[20px] font-extrabold text-[var(--text-primary)]"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            {t('dashboard.fromLines', { count: data.refLinesOpen })}
          </p>
        </ClickableCard>
      </motion.div>

      {/* ----- BUSINESSES ----- */}
      <motion.div variants={itemVariants}>
        <ClickableCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1">
                {t('dashboard.ecoTitle')}
              </p>
              <h2 className="font-display text-[18px] font-extrabold tracking-tight text-[var(--text-primary)]">
                {t('dashboard.txActivity')}
              </h2>
            </div>
            <GlassPill tone="plasma" dot compact>
              LIVE
            </GlassPill>
          </div>
          <BusinessActivity businesses={data.businesses} />
        </ClickableCard>
      </motion.div>

      {/* ----- АКТИВНОСТЬ КАПИТАЛА (заменил историю, она в кошельке) ----- */}
      <motion.div variants={itemVariants}>
        <PulseCard data={data} onGoWallet={() => {
          haptic.light();
          navigate('/wallet');
        }} />
      </motion.div>

      {/* ----- EARLY BIRD POOL ----- */}
      <motion.div variants={itemVariants}>
        <EarlyBirdPoolCard />
      </motion.div>

      {/* ----- CTA ----- */}
      <motion.div variants={itemVariants}>
        <ClickableCard
          variant="strong"
          glow="lunar"
          className="p-5"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="lg-pill !rounded-2xl h-11 w-11 flex items-center justify-center">
              <Sparkles
                size={20}
                className="text-[var(--accent-primary)]"
                strokeWidth={1.8}
              />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-[17px] font-extrabold tracking-tight text-[var(--text-primary)]">
                {t('dashboard.cta.title')}
              </h3>
              <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
                {t('dashboard.cta.subtitle')}
              </p>
            </div>
          </div>
          <GlassButton
            intent="primary"
            fullWidth
            trailing={<ArrowUpRight size={16} />}
            onClick={(e) => {
              e.stopPropagation();
              haptic.success();
              navigate('/slots');
            }}
          >
            {t('dashboard.cta.button')}
          </GlassButton>
        </ClickableCard>
      </motion.div>
    </motion.div>

    {/* ───── Modals ───── */}
    <NodeInfoPopup
      business={infoIdx !== null ? BUSINESSES[infoIdx] : null}
      onClose={() => setInfoIdx(null)}
    />
    <PcAutoInfoModal
      open={pcInfoOpen}
      pcNextClaimMs={apiData?.pcNextClaimMs ?? null}
      pcContracts={apiData?.pcContracts ?? []}
      manualPending={apiData?.manualPendingProfit ?? 0}
      onClose={() => setPcInfoOpen(false)}
    />
    <ClaimModal
      open={claimOpen}
      pendingProfit={apiData?.manualPendingProfit ?? 0}
      cycleSecondsLeft={cycleSecondsLeft}
      plasmaCoreActive={plasmaCoreActive}
      plasmaCoreCharge={plasmaCoreCharge}
      pcContractsCount={apiData?.pcContractsCount ?? 0}
      manualContractsCount={apiData?.manualContractsCount ?? 0}
      pcNextClaimMs={apiData?.pcNextClaimMs ?? null}
      pcPendingTotal={
        (apiData?.pcContracts ?? []).reduce((s, c) => s + (c.pending ?? 0), 0)
      }
      onClose={() => setClaimOpen(false)}
      onClaim={handleClaim}
    />
    </ConnectionStatusProvider>
  );
}

// ----------------------------------------------------------------------------
// Early Bird Pool — FOMO progress bar
// ----------------------------------------------------------------------------

function EarlyBirdPoolCard() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const [poolData, setPoolData] = useState<{ initial: number; balance: number } | null>(null);
  useEffect(() => {
    import('@/services/api').then(({ lunaApi }) => {
      lunaApi.pool.status().then((res) => {
        const d = res.data as { success: boolean; pool?: { initial: string; balance: string } };
        if (d.success && d.pool) {
          setPoolData({
            initial: parseFloat(d.pool.initial),
            balance: parseFloat(d.pool.balance),
          });
        }
      }).catch(() => { /* fallback к дефолту */ });
    });
  }, []);
  const initial = poolData?.initial ?? 1_000_000;
  const balance = poolData?.balance ?? 1_000_000;
  const spent = initial - balance;
  const pct = Math.round((balance / initial) * 100);
  const nearDepletion = pct < 20;

  return (
    <ClickableCard className="p-5 relative">
      <InfoTooltip
        tone="lunar"
        className="!top-[-2px] !right-[3px]"
        ariaLabel={t('dashboard.earlyBird.tooltipAria')}
        title={t('dashboard.earlyBird.title')}
        body={
          <Trans
            i18nKey="dashboard.earlyBird.tooltipBody"
            components={{ b: <strong />, br: <br /> }}
          />
        }
        bullets={[
          t('dashboard.earlyBird.tooltipBullet1'),
          t('dashboard.earlyBird.tooltipBullet2'),
          t('dashboard.earlyBird.tooltipBullet3'),
        ]}
      />
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] mb-1">
            {t('dashboard.earlyBird.title')}
          </p>
          <h2 className="font-display text-[18px] font-extrabold tracking-tight text-[var(--text-primary)]">
            ${balance.toLocaleString(locale)}
          </h2>
        </div>
        <GlassPill tone={nearDepletion ? 'negative' : 'positive'} dot compact className="!mt-[40px] self-start">
          {pct}%
        </GlassPill>
      </div>

      {/* Progress bar */}
      <div
        className="relative h-2.5 rounded-full overflow-hidden"
        style={{ background: 'var(--glass-fill)' }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: nearDepletion
              ? 'linear-gradient(90deg, var(--status-warning), var(--status-negative))'
              : 'linear-gradient(90deg, var(--accent-primary), var(--accent-secondary))',
            boxShadow: nearDepletion
              ? '0 0 12px var(--status-warning)'
              : '0 0 12px var(--accent-primary)',
          }}
        />
      </div>

      <div className="flex justify-between mt-2">
        <span className="text-[11px] text-[var(--text-muted)]">
          {t('dashboard.earlyBird.used', { amount: spent.toLocaleString(locale) })}
        </span>
        <span className="text-[11px] text-[var(--text-muted)]">
          {t('dashboard.earlyBird.initial', { amount: initial.toLocaleString(locale) })}
        </span>
      </div>
    </ClickableCard>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function MiniStat({
  label,
  value,
  accent,
  progress,
  onClick,
}: {
  label: string;
  value: string;
  accent?: boolean;
  progress?: number;
  onClick?: () => void;
}) {
  const { haptic } = useTelegramWebApp();
  const handleTap = onClick
    ? (e: React.MouseEvent) => {
        // Prevent bubbling to the parent hero card
        e.stopPropagation();
        haptic.light();
        onClick();
      }
    : undefined;

  return (
    <div
      onClick={handleTap}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={`lg-pill !rounded-2xl px-3 py-2.5 relative overflow-hidden h-full flex flex-col items-center justify-center text-center ${
        onClick ? 'cursor-pointer active:scale-[0.98] transition-transform' : ''
      }`}
    >
      {progress !== undefined && (
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none"
          style={{
            width: `${progress}%`,
            background:
              'linear-gradient(90deg, color-mix(in oklab, var(--accent-primary) 22%, transparent), transparent)',
          }}
        />
      )}
      {/* Маленький зелёный дот-пульс в верхнем правом углу — визуальный
          "живой" маркер, чтобы карточка не казалась полупустой при
          маленьких значениях (0.21% и т.п.). */}
      <span
        aria-hidden
        className="absolute h-1.5 w-1.5 rounded-full"
        style={{
          top: '13px',
          right: '13px',
          background: 'var(--status-positive)',
          boxShadow:
            '0 0 6px color-mix(in oklab, var(--status-positive) 80%, transparent)',
          animation: 'claim-dot-pulse 2s ease-in-out infinite',
        }}
      />
      <p className="relative z-10 text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
        {label}
      </p>
      <p
        className={
          accent
            ? 'relative z-10 text-[13px] font-semibold text-[var(--accent-primary)] mt-0.5'
            : 'relative z-10 text-[13px] font-semibold text-[var(--text-primary)] mt-0.5'
        }
      >
        {value}
      </p>
    </div>
  );
}

function SlotsSummaryCard({
  summary,
  onClick,
}: {
  summary: SlotSummaryEntry[] | undefined;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const handleTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic.light();
    onClick();
  };

  const list = summary ?? [];
  const visible = list.slice(0, SLOTS_VISIBLE);
  const restCount = list
    .slice(SLOTS_VISIBLE)
    .reduce((sum, s) => sum + s.count, 0);
  const label = list.length > 1 ? t('dashboard.slots') : t('dashboard.slot');

  return (
    <div
      onClick={handleTap}
      role="button"
      tabIndex={0}
      className="lg-pill !rounded-2xl px-3 py-2.5 relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform h-full flex flex-col items-center justify-center text-center"
    >
      <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
        {label}
      </p>
      {list.length === 0 ? (
        <p className="text-[13px] font-semibold text-[var(--text-primary)] mt-0.5">
          {t('dashboard.noActiveSlots')}
        </p>
      ) : (
        <div className="mt-0.5 space-y-0.5 w-full">
          {visible.map((s, idx) => {
            const isLast = idx === visible.length - 1;
            const showRest = isLast && restCount > 0;
            return (
              <div
                key={s.name}
                className="flex items-baseline justify-center gap-2 text-[13px] font-semibold text-[var(--text-primary)]"
              >
                <span className="truncate">
                  {s.name}&nbsp;×{s.count}
                </span>
                {showRest && (
                  <span className="text-[11px] font-medium text-[var(--text-muted)] whitespace-nowrap">
                    {t('dashboard.moreCount', { count: restCount })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// PulseCard — мини-аналитика капитала (заменила историю на главной).
// Показывает доход за 24ч/30д, партнёрский взнос, прогресс партнёрских
// линий, активные контракты и таймер до следующего клейма.
// ────────────────────────────────────────────────────────────────────
function formatCountdown(seconds: number, t: TFunction): string {
  if (seconds <= 0) return t('dashboard.pulse.nowReady');
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return t('dashboard.pulse.countdown.h', { h, m: String(m).padStart(2, '0') });
  if (m > 0) return t('dashboard.pulse.countdown.m', { m, s: String(s).padStart(2, '0') });
  return t('dashboard.pulse.countdown.s', { s });
}

function PulseCard({
  data,
  onGoWallet,
}: {
  data: DashboardUserData;
  onGoWallet: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  // 10 линий в глубину согласно luna_referral_config.
  const linesPct = Math.min(100, (data.refLinesOpen / 10) * 100);

  return (
    <ClickableCard className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-[18px] font-extrabold tracking-tight text-[var(--text-primary)]">
          {t('dashboard.pulse.title')}
        </h2>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onGoWallet();
          }}
          className="text-[12px] text-[var(--accent-secondary)] inline-flex items-center gap-1"
        >
          {t('dashboard.pulse.history')}
          <ArrowUpRight size={14} strokeWidth={2} />
        </button>
      </div>

      {/* Три тайла: 24ч / 30д / Партнёры */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <PulseTile
          label={t('dashboard.pulse.hours24')}
          value={data.todayReward}
          accent="var(--status-positive)"
        />
        <PulseTile
          label={t('dashboard.pulse.days30')}
          value={data.month30Reward}
          accent="var(--accent-secondary)"
        />
        <PulseTile
          label={t('dashboard.pulse.partners')}
          value={data.refIncome}
          accent="var(--accent-primary)"
          sub={`${data.refLinesOpen} ${pluralLines(data.refLinesOpen, t, lang)}`}
          subColor="var(--text-muted)"
        />
      </div>

      {/* Прогресс-бары */}
      <div className="space-y-3.5">
        <ProgressRow
          label={t('dashboard.pulse.partnerLines')}
          right={`${data.refLinesOpen} / 10`}
          pct={linesPct}
          accent="var(--accent-primary)"
        />
        <ProgressRow
          label={t('dashboard.pulse.activeContracts')}
          right={t('dashboard.pulse.contractsUnit', { count: data.activeContractsCount })}
          pct={data.activeContractsCount > 0 ? 100 : 0}
          accent="var(--accent-secondary)"
          solid
        />
      </div>

      {/* Таймер ближайшего события — МИНИМУМ среди ручного клейма
          и ближайшего PC-автосбора. Раньше показывался только
          ручной cycleSecondsLeft, из-за чего если у юзера PC-слот
          должен сработать через 2 часа, а ручной через 23 —
          писалось 23ч (неверно). Теперь показываем что реально
          случится раньше. */}
      {(() => {
        const manualSec = data.cycleSecondsLeft;
        const pcSec =
          data.pcNextClaimMs !== null
            ? Math.max(
                0,
                Math.floor((data.pcNextClaimMs - Date.now()) / 1000),
              )
            : null;
        const candidates: number[] = [];
        if (manualSec > 0 && data.manualContractsCount > 0) {
          candidates.push(manualSec);
        }
        if (pcSec !== null && data.pcContractsCount > 0) {
          candidates.push(pcSec);
        }
        const nearest =
          candidates.length > 0 ? Math.min(...candidates) : 0;
        return (
          <div
            className="mt-4 flex items-center justify-between rounded-xl px-3 py-2.5"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
            }}
          >
            <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--text-muted)]">
              {t('dashboard.pulse.nextEvent')}
            </span>
            <span
              className="text-[13px] font-mono font-extrabold tabular-nums"
              style={{ color: 'var(--accent-secondary)' }}
            >
              {formatCountdown(nearest, t)}
            </span>
          </div>
        );
      })()}
    </ClickableCard>
  );
}

function pluralLines(n: number, t: TFunction, lang: 'ru' | 'en'): string {
  if (lang === 'en') return n === 1 ? t('dashboard.pulse.linesOne') : t('dashboard.pulse.linesMany');
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return t('dashboard.pulse.linesMany');
  if (m10 === 1) return t('dashboard.pulse.linesOne');
  if (m10 >= 2 && m10 <= 4) return t('dashboard.pulse.linesFew');
  return t('dashboard.pulse.linesMany');
}

function PulseTile({
  label,
  value,
  accent,
  sub,
  subColor,
}: {
  label: string;
  value: number;
  accent: string;
  sub?: string;
  subColor?: string;
}) {
  return (
    <div
      className="rounded-2xl p-3"
      style={{
        background: `color-mix(in oklab, ${accent} 8%, transparent)`,
        border: `1px solid color-mix(in oklab, ${accent} 22%, transparent)`,
      }}
    >
      <p className="text-[9px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)] mb-1">
        {label}
      </p>
      <div
        className="text-[13px] font-extrabold tabular-nums truncate"
        style={{ color: accent }}
      >
        <LiquidNumber
          value={value}
          decimals={2}
          prefix={value > 0 ? '+' : ''}
          suffix=" USD₮"
        />
      </div>
      {sub && (
        <p
          className="text-[10px] font-mono mt-0.5 truncate"
          style={{ color: subColor ?? 'var(--text-muted)' }}
        >
          {sub}
        </p>
      )}
    </div>
  );
}

function ProgressRow({
  label,
  right,
  pct,
  accent,
  solid,
}: {
  label: string;
  right: string;
  pct: number;
  accent: string;
  solid?: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-[var(--text-muted)]">
          {label}
        </span>
        <span
          className="text-[12px] font-extrabold tabular-nums"
          style={{ color: accent }}
        >
          {right}
        </span>
      </div>
      <div
        className="h-1.5 w-full rounded-full overflow-hidden"
        style={{ background: 'color-mix(in oklab, var(--text-muted) 18%, transparent)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: solid
              ? accent
              : `linear-gradient(90deg, color-mix(in oklab, ${accent} 60%, transparent), ${accent})`,
            boxShadow: `0 0 10px color-mix(in oklab, ${accent} 40%, transparent)`,
          }}
        />
      </div>
    </div>
  );
}
